/**
 * File: apps/qbench-cp/reactapp/src/shared/utils/form.js
 *
 * Purpose: Form-field helpers covering DOM prop filtering, tag-field detection, and
 *     date/datetime conversion between the API's UTC format and local display format.
 *
 * Scope: react-hook-form field rendering and date/datetime value conversion for the
 *     customer portal's order/sample/test forms.
 *
 * Tags: customer-portal
 *
 * Overview: `domProps` whitelists a fixed set of keys safe to spread onto a native DOM
 *     element. `isTagsField` checks whether a field config represents the TAGS additional
 *     field. `getDateFormat` returns the date-only or date+time format string. `toLocalDate`
 *     parses a server value into a moment, converting UTC-to-local only when `isDateTime` is
 *     true (date-only values are parsed in local time as-is to avoid day-shifting).
 *     `fromLocalDate` mirrors this in reverse, formatting a moment (or string) back to the
 *     server format, cloning before calling `.utc()` for datetime values to avoid mutating
 *     the original moment. `toSelectOptions`/`toObjRelSelectOptions`/`parseObjRelDefault`
 *     convert raw values or object-relation dictionaries into react-select-compatible
 *     option/value shapes, stripping parenthetical suffixes from labels.
 *
 * Dependencies: See .claude/rules/form-utils.md for the date-only vs datetime conversion
 *     and moment-mutation constraints this file must respect.
 *
 * Exports: domProps, isTagsField, getDateFormat, toLocalDate, fromLocalDate,
 *     toSelectOptions, toObjRelSelectOptions, parseObjRelDefault
 */

import moment from 'moment';
import { AddlFieldType, TagFieldNames } from 'shared/constants/fields';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
} from 'shared/constants/misc';
import { isNilOrEmptyIterables } from './functions';

export function domProps(props) {
  return Object.fromEntries(
    Object.entries(props).filter(([key]) =>
      [
        'id',
        'className',
        'style',
        'type',
        'htmlFor',
        'onWheel',
        'step',
      ].includes(key),
    ),
  );
}

export function isTagsField(field) {
  return (
    field.fieldType === AddlFieldType.TAG && field.name === TagFieldNames.TAGS
  );
}

export function getDateFormat(isDateTime = false) {
  return isDateTime
    ? `${DEFAULT_DATE_FORMAT} ${DEFAULT_TIME_FORMAT}`
    : DEFAULT_DATE_FORMAT;
}

export function toLocalDate(dateStr, isDateTime = false) {
  if (!dateStr) return null;

  const dateFormat = getDateFormat(isDateTime);
  const dt = isDateTime
    ? moment.utc(dateStr, dateFormat)
    : moment(dateStr, dateFormat);
  if (!dt.isValid()) return null;

  return isDateTime ? dt.local() : dt;
}

export function fromLocalDate(dateStr, isDateTime = false) {
  if (!dateStr) return null;

  const dateFormat = getDateFormat(isDateTime);
  const isMoment = moment.isMoment(dateStr);
  const dt = isMoment ? dateStr : moment(dateStr, dateFormat);
  if (!dt.isValid()) return null;

  if (isDateTime) {
    return dt.clone().utc().format(dateFormat);
  }
  return dt.format(dateFormat);
}

export function toSelectOptions(value) {
  return Array.isArray(value)
    ? value.map(toSelectOptions)
    : { label: value, value };
}

export function toObjRelSelectOptions(value) {
  const values = Object.entries(value).map(([key, val]) => ({
    value: key,
    // Note: Can also do: val.replace(`${key} `, '').replace(`— `, ''),
    label: val.split('—').at(-1).trim(),
  }));
  return values.length === 1 ? values.at(0) : values;
}

export function parseObjRelDefault(obj, fallback) {
  if (isNilOrEmptyIterables(obj)) return fallback || null;

  const entries = Object.entries(obj);
  const result = entries.map(([key, val]) => {
    const stringValue = String(val);
    const numericValue = parseInt(key, 10);
    const value = isNaN(numericValue) ? key : numericValue;

    let label = stringValue;
    const separator = ' — ';
    const separatorIndex = stringValue.indexOf(separator);

    if (separatorIndex !== -1) {
      label = stringValue.substring(separatorIndex + separator.length);
      if (!label || label.trim() === '') {
        label = stringValue;
      }
    }

    // Remove parentheses and the content within
    label = label.replace(/\s*\([^)]*\)/g, '').trim();

    return { label, value };
  });

  return entries.length === 1 ? result.at(0) : result;
}
